import { queryOptions } from "@tanstack/react-query";
import { call } from "./api";
import { dayPasses, folded, optionPasses, textPasses, type SortState } from "./columnFilters";
import { addressBody, adrLinesToSend, lineBody, newKey, readDecimal, stopBody, type AdrLineState, type LineState, type OrderFormSettings, type StopState } from "./orderEntry";
import type { StockLine } from "./stock";

// Uitleveren and Aanleveren (docs/partner-portal.md §16 step 8; H-w01 … H-w03): an order for our warehouse, with the
// transport beside it when Willems drives. The customer sees one order; that a warehouse order and a transport order
// are behind it shows nowhere. The articles come from the customer's stock, asked as Voorraad asks it.

export type WarehouseKind = "outbound" | "inbound";
export type WarehouseTransport = "willems" | "customer";

/** A VAS code the customer may choose for an uitlevering (Willems ticks them in Warehouse → VAS). */
export interface VasChoice {
  code: string;
  description: string | null;
  descriptionEn: string | null;
  /** header: for the whole order; article: for a line; both: either. */
  appliesTo: "header" | "article" | "both" | null;
}

export interface WarehouseOptions {
  owners: string[];
  /** stockCode: the warehouse as the stock names it (its id in the WMS). */
  warehouses: { code: string; name: string; stockCode: string }[];
  vasCodes?: VasChoice[];
}

/** The codes for the whole order or for a line, as the code's type allows. */
export function vasFor(choices: VasChoice[] | undefined, level: "header" | "article"): VasChoice[] {
  return (choices ?? []).filter((choice) => choice.appliesTo === level || choice.appliesTo === "both");
}

/** What a code does, in Dutch or, for another language, in English when Willems has it. */
export function vasText(choice: VasChoice, language: string): string {
  return (language.startsWith("nl") ? choice.description : (choice.descriptionEn ?? choice.description)) ?? choice.code;
}

export const warehouseOptionsQuery = queryOptions({
  queryKey: ["partner", "warehouse-orders", "options"],
  queryFn: () => call<WarehouseOptions>("GET", "/partner/warehouse-orders/options"),
  staleTime: 60_000,
});

export interface WarehouseLineState {
  key: string;
  article: string;
  quantity: string;
  batch: string;
  /** Aanleveren: the unit chosen of the article's (cu, tu, du); empty is its first. */
  level: string;
  /** Uitleveren: the pallets ticked from the stock (PalletChoice.key); then each goes as a line of its own. */
  pallets: string[];
  /** Uitleveren: the VAS codes for this line (16-10-2026). */
  vas: string[];
}

export function emptyWarehouseLine(): WarehouseLineState {
  return { key: newKey(), article: "", quantity: "", batch: "", level: "", pallets: [], vas: [] };
}

/** One of the customer's own articles (W11), with the units Scoretrace knows it in: the box first. */
export interface CatalogArticle {
  code: string;
  description: string;
  units: { level: "cu" | "tu" | "du"; unit: string; unitPk: number | null }[];
}

export function warehouseArticlesQuery(owner: string) {
  return queryOptions({
    queryKey: ["partner", "warehouse-orders", "articles", owner],
    queryFn: () =>
      call<{ articles: CatalogArticle[] }>("GET", `/partner/warehouse-orders/articles?${new URLSearchParams({ owner }).toString()}`).then((body) => body.articles),
    staleTime: 5 * 60_000,
  });
}

/** The unit a line of an aanlevering is in: the one chosen, or the article's first. */
export function catalogUnit(article: CatalogArticle | undefined, level: string) {
  if (!article) return undefined;
  return article.units.find((unit) => unit.level === level) ?? article.units[0];
}

export interface WarehouseFormState {
  kind: WarehouseKind;
  warehouse: string;
  owner: string;
  customerReference: string;
  /** Outbound: the day it leaves our warehouse; inbound without Willems: the day it arrives. */
  day: string;
  transport: WarehouseTransport;
  /** With Willems: outbound where it is delivered, inbound where it is collected. Collected: where it goes (no day). */
  stop: StopState;
  /** With Willems: what the transport carries, and what it asks of the transport (the customer's own ticked). */
  goods: LineState[];
  requirementIds: string[];
  /** With Willems: the cubic metres, and the ADR code with ADR ticked (20-10-2026). */
  volumeM3?: string;
  adrLines?: AdrLineState[];
  lines: WarehouseLineState[];
  remark: string;
  /** Uitleveren: the VAS codes for the whole order. */
  vasCodes: string[];
}

/** An article of the stock, for the lines: what is free in its unit, in the chosen warehouse and owner. */
export interface ArticleChoice {
  article: string;
  description: string | null;
  unit: string | null;
  unitPk: number | null;
  free: number;
}

export function articleChoices(lines: StockLine[], stockCode: string | null, owner: string | null): ArticleChoice[] {
  const byArticle = new Map<string, ArticleChoice>();
  for (const line of lines) {
    if (stockCode && line.warehouse.code !== stockCode) continue;
    if (owner && line.owner.code !== owner) continue;
    const found = byArticle.get(line.article) ?? { article: line.article, description: line.description, unit: line.unit, unitPk: line.unitPk, free: 0 };
    if (line.status === "free") found.free += line.quantity;
    if (!found.description && line.description) found.description = line.description;
    if (found.unitPk === null && line.unitPk !== null) {
      found.unitPk = line.unitPk;
      found.unit = line.unit;
    }
    byArticle.set(line.article, found);
  }
  return [...byArticle.values()].sort((a, b) => (a.description ?? a.article).localeCompare(b.description ?? b.article));
}

/** An article to find in the field Artikel: its code, what it is, and what to show beside it (on an uitlevering what is free). */
export interface ArticleOption {
  code: string;
  description: string | null;
  meta?: string;
}

/**
 * The articles that hold every word typed, in their code or description, without case or accents (Axxent, 02-11-2026:
 * "Ik kan geen artikelnummers intoetsen"). A code that is the text comes first, then codes that start with it, then
 * the list's own order.
 */
export function findArticles<T extends ArticleOption>(options: T[], query: string): T[] {
  const words = folded(query).split(/\s+/).filter(Boolean);
  if (words.length === 0) return options;
  const text = folded(query.trim());
  const rank = (option: T) => {
    const code = folded(option.code);
    return code === text ? 0 : code.startsWith(text) ? 1 : 2;
  };
  return options
    .filter((option) => {
      const haystack = `${folded(option.code)} ${folded(option.description ?? "")}`;
      return words.every((word) => haystack.includes(word));
    })
    .map((option, index) => ({ option, index, rank: rank(option) }))
    .sort((a, b) => a.rank - b.rank || a.index - b.index)
    .map((entry) => entry.option);
}

/** A free pallet of an article in the stock, to tick for an uitlevering (14-10-2026). */
export interface PalletChoice {
  /** One stock line: a pallet with two batches on it is two choices. */
  key: string;
  sscc: string | null;
  palletNumber: string | null;
  batch: string | null;
  bestBefore: string | null;
  quantity: number;
  unit: string | null;
  unitPk: number | null;
}

/** The free pallets of each article, in the chosen warehouse and owner: the shortest best before first. */
export function palletChoices(lines: StockLine[], stockCode: string | null, owner: string | null): Map<string, PalletChoice[]> {
  const byArticle = new Map<string, PalletChoice[]>();
  lines.forEach((line, index) => {
    if (line.status !== "free" || line.quantity <= 0) return;
    if (stockCode && line.warehouse.code !== stockCode) return;
    if (owner && line.owner.code !== owner) return;
    const list = byArticle.get(line.article) ?? [];
    list.push({
      key: `${line.sscc ?? ""}|${line.palletNumber ?? ""}|${line.batch ?? ""}|${index}`,
      sscc: line.sscc,
      palletNumber: line.palletNumber,
      batch: line.batch,
      bestBefore: line.bestBefore,
      quantity: line.quantity,
      unit: line.unit,
      unitPk: line.unitPk,
    });
    byArticle.set(line.article, list);
  });
  for (const list of byArticle.values()) {
    list.sort((a, b) => (a.bestBefore ?? "9999").localeCompare(b.bestBefore ?? "9999") || (a.sscc ?? "").localeCompare(b.sscc ?? ""));
  }
  return byArticle;
}

/** The batches of an article that are free, with how much: to choose from, or to type another. */
export function batchChoices(pallets: PalletChoice[]): { batch: string; free: number }[] {
  const free = new Map<string, number>();
  for (const pallet of pallets) if (pallet.batch) free.set(pallet.batch, (free.get(pallet.batch) ?? 0) + pallet.quantity);
  return [...free.entries()].map(([batch, quantity]) => ({ batch, free: quantity })).sort((a, b) => a.batch.localeCompare(b.batch));
}

/**
 * The pallets to tick on a line with a batch typed or chosen (20-10-2026): only those whose batch holds what is typed,
 * and the ones already ticked. Without a batch, all of them.
 */
export function palletsOfBatch(pallets: PalletChoice[], batch: string, ticked: readonly string[]): PalletChoice[] {
  const wanted = batch.trim().toLowerCase();
  if (!wanted) return pallets;
  return pallets.filter((pallet) => ticked.includes(pallet.key) || (pallet.batch ?? "").toLowerCase().includes(wanted));
}

/** The pallets ticked on a line, as they are in the stock now. */
export function chosenPallets(line: WarehouseLineState, pallets: ReadonlyMap<string, PalletChoice[]>): PalletChoice[] {
  if (line.pallets.length === 0) return [];
  const chosen = new Set(line.pallets);
  return (pallets.get(line.article.trim()) ?? []).filter((pallet) => chosen.has(pallet.key));
}

/** The lines asking more than is free (outbound): Willems contacts the customer, sending still works (W15). */
export function overFree(form: WarehouseFormState, choices: ReadonlyMap<string, ArticleChoice>): string[] {
  if (form.kind !== "outbound") return [];
  return form.lines
    .filter((line) => {
      // Pallets ticked from the stock are free by definition.
      if (line.pallets.length > 0) return false;
      const choice = choices.get(line.article.trim());
      const asked = readDecimal(line.quantity) ?? 0;
      return choice !== undefined && asked > choice.free;
    })
    .map((line) => line.article.trim());
}

export function linesComplete(form: WarehouseFormState): boolean {
  const filled = form.lines.filter((line) => line.article.trim() || line.quantity.trim() || line.pallets.length > 0);
  return filled.length > 0 && filled.every((line) => line.article.trim() && (line.pallets.length > 0 || (readDecimal(line.quantity) ?? 0) > 0));
}

/** The order as POST /partner/warehouse-orders takes it. */
export function warehouseOrderBody(
  form: WarehouseFormState,
  choices: ReadonlyMap<string, ArticleChoice>,
  settings: OrderFormSettings | undefined,
  catalog: ReadonlyMap<string, CatalogArticle> = new Map(),
  pallets: ReadonlyMap<string, PalletChoice[]> = new Map(),
) {
  const willems = form.transport === "willems";
  return {
    kind: form.kind,
    warehouse: form.warehouse,
    ...(form.owner ? { owner: form.owner } : {}),
    customerReference: form.customerReference.trim(),
    day: form.kind === "inbound" && willems ? form.stop.date : form.day,
    transport: form.transport,
    stop: willems ? stopBody(form.stop) : null,
    // Collected: where it goes, for the CMR and the warehouse (19-10-2026).
    address: form.kind === "outbound" && !willems ? addressBody(form.stop.address) : null,
    ...(willems
      ? { requirementIds: form.requirementIds, volumeM3: readDecimal(form.volumeM3 ?? ""), adrLines: adrLinesToSend(form.requirementIds, form.adrLines, settings) }
      : {}),
    // An aanlevering the customer brings: what comes on what, for the warehouse and the copy to TALIS (23-10-2026).
    goods: willems || form.kind === "inbound" ? form.goods.map((line) => lineBody(line, settings)) : null,
    remark: form.remark.trim() || null,
    ...(form.kind === "outbound" ? { vasCodes: form.vasCodes } : {}),
    lines: form.lines
      .filter((line) => line.article.trim() && (line.pallets.length > 0 || (readDecimal(line.quantity) ?? 0) > 0))
      .flatMap((line) => {
        // Uitleveren with pallets ticked: one line per pallet, with all that is on it, its batch, number and SSCC.
        const ticked = form.kind === "outbound" ? chosenPallets(line, pallets) : [];
        if (ticked.length > 0) {
          const choice = choices.get(line.article.trim());
          return ticked.map((pallet) => ({
            article: line.article.trim(),
            description: choice?.description ?? null,
            quantity: pallet.quantity,
            unit: pallet.unit ?? choice?.unit ?? null,
            unitPk: pallet.unitPk ?? choice?.unitPk ?? null,
            batch: pallet.batch,
            palletNumber: pallet.palletNumber,
            sscc: pallet.sscc,
            vasCodes: line.vas,
            remark: null,
          }));
        }
        // Aanleveren with one of the customer's articles: in the unit chosen; otherwise as the stock has it.
        const known = form.kind === "inbound" ? catalog.get(line.article.trim()) : undefined;
        const unit = catalogUnit(known, line.level);
        const choice = choices.get(line.article.trim());
        return {
          article: line.article.trim(),
          description: known?.description ?? choice?.description ?? null,
          quantity: readDecimal(line.quantity)!,
          unit: known ? (unit?.unit ?? null) : (choice?.unit ?? null),
          unitPk: known ? (unit?.unitPk ?? null) : (choice?.unitPk ?? null),
          batch: line.batch.trim() || null,
          palletNumber: null,
          sscc: null,
          vasCodes: form.kind === "outbound" ? line.vas : [],
          remark: null,
        };
      }),
  };
}

export interface SentWarehouseOrder {
  order: { number: string; kind: WarehouseKind; status: "sent" | "received" | "waiting" };
  /** With Willems driving: the shipment, as Zendingen shows it. */
  shipment: string | null;
}

export function sendWarehouseOrder(body: ReturnType<typeof warehouseOrderBody>, key: string) {
  return call<SentWarehouseOrder>("POST", "/partner/warehouse-orders", body, { "X-Idempotency-Key": key });
}

// ─── Magazijnorders (26-10-2026): what the customer sent to our warehouse, also when it is collected or brought ─────

/** Sent: on its way to the warehouse; received: the warehouse has it; waiting: Willems looks at it first. */
export type WarehouseOrderStatus = "sent" | "received" | "waiting";

export const WAREHOUSE_STATUS_VARIANT: Record<WarehouseOrderStatus, string> = {
  sent: "planned",
  received: "planned",
  waiting: "onhold",
};

export interface WarehouseOrderAddress {
  name: string;
  street: string | null;
  houseNumber: string | null;
  houseNumberAddition: string | null;
  postalCode: string | null;
  city: string;
  countryCode: string;
}

export interface WarehouseOrderLineView {
  article: string;
  description: string | null;
  quantity: number;
  unit: string | null;
  batch: string | null;
  palletNumber?: string | null;
  sscc?: string | null;
  vas: VasChoice[];
}

export interface PortalWarehouseOrder {
  number: string;
  kind: WarehouseKind;
  customerReference: string;
  /** Outbound: the day it leaves our warehouse; inbound: the day it arrives. */
  day: string;
  deliveryDay: string | null;
  transport: WarehouseTransport;
  /** Collected: where it goes. */
  address: WarehouseOrderAddress | null;
  remark: string | null;
  vas: VasChoice[];
  lines: WarehouseOrderLineView[];
  status: WarehouseOrderStatus;
  owner: string;
  createdAt: string;
  warehouse: { code: string; name: string };
  /** With Willems driving: the shipment, as Zendingen shows it. */
  shipment: string | null;
  enteredVia: "portal" | "willems";
}

export const warehouseOrdersQuery = queryOptions({
  queryKey: ["partner", "warehouse-orders", "list"],
  queryFn: () => call<{ orders: PortalWarehouseOrder[] }>("GET", "/partner/warehouse-orders").then((body) => body.orders),
  staleTime: 30_000,
});

export const WAREHOUSE_ORDER_VIEWS = ["all", "outbound", "inbound"] as const;
export type WarehouseOrderView = (typeof WAREHOUSE_ORDER_VIEWS)[number];

/** One piece of text in a place the customer would search: their reference, the numbers, an article, a batch. */
function holds(order: PortalWarehouseOrder, text: string): boolean {
  const places = [
    order.number,
    order.customerReference,
    order.shipment ?? "",
    order.address?.name ?? "",
    order.address?.city ?? "",
    ...order.lines.flatMap((line) => [line.article, line.description ?? "", line.batch ?? "", line.sscc ?? ""]),
  ];
  return places.some((place) => place.toLowerCase().includes(text));
}

/** The orders of a view that hold the search: the whole text in one place, or every word somewhere. */
export function filterWarehouseOrders(orders: PortalWarehouseOrder[], filter: { view: WarehouseOrderView; q?: string }): PortalWarehouseOrder[] {
  const text = filter.q?.trim().toLowerCase() ?? "";
  const words = text.split(/\s+/).filter(Boolean);
  return orders.filter(
    (order) =>
      (filter.view === "all" || order.kind === filter.view) &&
      (!text || holds(order, text) || (words.length > 1 && words.every((word) => holds(order, word)))),
  );
}

// Sorting and filtering per column (26-10-2026), as on Zendingen: the list is here whole, so the browser does both.

export const WAREHOUSE_ORDER_COLUMNS = ["order", "kind", "warehouse", "day", "transport", "articles", "status"] as const;
export type WarehouseOrderColumn = (typeof WAREHOUSE_ORDER_COLUMNS)[number];

/** The states in the order an order goes through them: Willems looks at it, sent, at the warehouse. */
export const WAREHOUSE_ORDER_STATUSES: WarehouseOrderStatus[] = ["waiting", "sent", "received"];

/** What a column sorts on: the reference, the kind, the warehouse's name, the day, Willems first, the first article. */
function sortKey(order: PortalWarehouseOrder, column: WarehouseOrderColumn): string | number {
  switch (column) {
    case "order":
      return folded(order.customerReference);
    case "kind":
      return order.kind;
    case "warehouse":
      return folded(order.warehouse.name);
    case "day":
      return order.day;
    case "transport":
      return order.transport === "willems" ? 0 : 1;
    case "articles":
      return folded(order.lines[0]?.description ?? order.lines[0]?.article ?? "");
    case "status":
      return WAREHOUSE_ORDER_STATUSES.indexOf(order.status);
  }
}

/** The orders in the order of a column, up or down, then the newest first; without a column as they came (newest first). */
export function sortWarehouseOrders(orders: PortalWarehouseOrder[], sort: SortState): PortalWarehouseOrder[] {
  const column = WAREHOUSE_ORDER_COLUMNS.find((candidate) => candidate === sort.sort);
  if (!column) return orders;
  const direction = sort.order === "desc" ? -1 : 1;
  return orders
    .map((order, index) => ({ order, index, key: sortKey(order, column) }))
    .sort((a, b) => (a.key < b.key ? -direction : a.key > b.key ? direction : a.index - b.index))
    .map((entry) => entry.order);
}

/**
 * The orders that pass every column filter: the order on its reference, number and shipment; the kind, the warehouse,
 * the transport and the state as a choice; the day as days; the articles on article, description, batch and SSCC.
 */
export function columnFilterWarehouseOrders(
  orders: PortalWarehouseOrder[],
  filters: Partial<Record<WarehouseOrderColumn, string>>,
): PortalWarehouseOrder[] {
  const passes = (order: PortalWarehouseOrder, column: WarehouseOrderColumn, filter: string): boolean => {
    switch (column) {
      case "order":
        return textPasses(filter, [order.customerReference, order.number, order.shipment]);
      case "kind":
        return optionPasses(filter, order.kind);
      case "warehouse":
        return optionPasses(filter, order.warehouse.code);
      case "day":
        return dayPasses(filter, order.day);
      case "transport":
        return optionPasses(filter, order.transport);
      case "articles":
        return textPasses(
          filter,
          order.lines.flatMap((line) => [line.article, line.description, line.batch, line.sscc]),
        );
      case "status":
        return optionPasses(filter, order.status);
    }
  };
  const active = WAREHOUSE_ORDER_COLUMNS.filter((column) => filters[column]?.trim());
  return orders.filter((order) => active.every((column) => passes(order, column, filters[column]!)));
}

/** The warehouses in the list, once each, by name: the choices of the filter on Magazijn. */
export function warehousesOf(orders: PortalWarehouseOrder[]): { code: string; name: string }[] {
  const byCode = new Map(orders.map((order) => [order.warehouse.code, order.warehouse]));
  return [...byCode.values()].sort((a, b) => a.name.localeCompare(b.name));
}

export function countWarehouseOrders(orders: PortalWarehouseOrder[]): Record<WarehouseOrderView, number> {
  return {
    all: orders.length,
    outbound: orders.filter((order) => order.kind === "outbound").length,
    inbound: orders.filter((order) => order.kind === "inbound").length,
  };
}
